/** @jsxImportSource @emotion/react */
import _ from "lodash";
import { useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

import Colors from "styles/colors";

import { Alert } from "components/atoms/Alert.atom";
import { Button } from "components/atoms/Button.atom";
import { Text, FontSize } from "components/atoms/Text.atom";
import { FlexDiv, FlexColDiv } from "styles/container-elements";
import { PaginationControls } from "./PaginationControls";
import { getColors } from "../utils/chart.utils";

const badgeCss = {
  backgroundColor: Colors.inventoryView.PILL,
  borderRadius: "4px",
  color: "white",
  padding: "2px 0.5em",
};

type LOCATIONDATA = {
  id: number;
  code: string;
  name: string;
  vinCount: number;
  capacity?: number;
  overage?: number;
  timeZone?: string;
};

type TableRowProps = {
  data: LOCATIONDATA;
  showTotalCapacity: boolean;
  showCurrentCapacity: boolean;
  rowClickHandler: (locationId: number, data: LOCATIONDATA) => void;
};

const TableRow = ({
  data,
  showTotalCapacity,
  showCurrentCapacity,
  rowClickHandler,
}: TableRowProps) => {
  const colorCode = getColors("inventory", data.vinCount, data.capacity);
  return (
    <FlexDiv
      css={{
        textOverflow: "auto",
        overflow: "none",
        backgroundColor: Colors.inventoryView.LIGHTER_GRAY,
        padding: "0.5em 1em",
        borderRadius: "4px",
        flexDirection: "row",
        cursor: "pointer",
        border: `1px solid ${Colors.inventoryView.LIGHTER_GRAY}`,
        "&:hover": {
          boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.5)",
        },
      }}
      onClick={() => rowClickHandler(data.id, data)}
    >
      <FlexDiv style={{ flexGrow: 2 }}>
        <Text>
          ({data.code}) {data.name}
        </Text>
      </FlexDiv>
      <FlexDiv>
        {showTotalCapacity && showCurrentCapacity ? (
          <>
            <Text color={colorCode} bold style={{ marginRight: "5px" }}>
              {data.capacity && data.capacity > 0 ? (
                <>
                  {data.vinCount}/{data.capacity}
                </>
              ) : (
                <>{data.vinCount}</>
              )}
            </Text>
            {data.capacity && data.capacity > 0 ? (
              <Text
                bold
                style={{
                  backgroundColor: colorCode,
                  borderRadius: "4px",
                  color: "white",
                  padding: "2px 0.5em",
                }}
              >
                {data?.overage && data.overage > 0 ? "+" : null}
                {data.overage}
              </Text>
            ) : (
              <Text
                bold
                style={{
                  backgroundColor: colorCode,
                  borderRadius: "4px",
                  color: "white",
                  padding: "2px 0.5em",
                }}
              >
                {data.capacity}
              </Text>
            )}
          </>
        ) : (
          <Text bold style={badgeCss}>
            {data.vinCount}
          </Text>
        )}
      </FlexDiv>
    </FlexDiv>
  );
};

type InventoryLocationsListData = {
  inventoryItemsList: LOCATIONDATA[];
  title: string;
  totalPages?: number;
  pageSize: number;
  displayPaginationControls: boolean;
  displayShowMoreButton?: boolean;
  showMoreClickHandler?: () => void;
  showTotalCapacity?: boolean;
  showCurrentCapacity?: boolean;
  locationRowClickHandler: (locationId: number, data: LOCATIONDATA) => void;
};

export const InventoryLocationsList = ({
  inventoryItemsList,
  title,
  showMoreClickHandler,
  locationRowClickHandler,
  displayShowMoreButton = false,
  displayPaginationControls = false,
  pageSize = 10,
  showTotalCapacity = false,
  showCurrentCapacity = false,
}: InventoryLocationsListData) => {
  const { t } = useTranslation("inv-dashboard");

  const [page, setPage] = useState(1);

  const totalPages = _.ceil(inventoryItemsList.length / pageSize);

  const startIndex = (page - 1) * pageSize;
  const data = inventoryItemsList.slice(startIndex, startIndex + pageSize);

  const handlePrevClick = useCallback(() => {
    setPage((prevPage) => Math.max(prevPage - 1, 1));
  }, []);

  const handleNextClick = useCallback(() => {
    setPage((prevPage) => Math.min(prevPage + 1, totalPages));
  }, [totalPages]);

  const hasData = data.length > 0 ? true : false;

  return (
    <div
      style={{
        padding: "10px 15px",
        border: "1px solid",
        borderColor: Colors.inventoryView.LIGHTER_GRAY,
      }}
    >
      <Text size={FontSize.size18} bold>
        {title}
      </Text>
      <FlexColDiv
        style={{
          marginTop: "1em",
          gap: "10px",
        }}
      >
        {hasData &&
          data.map((item) => {
            return (
              <TableRow
                key={uuidv4()}
                data={item}
                showTotalCapacity={showTotalCapacity}
                showCurrentCapacity={showCurrentCapacity}
                rowClickHandler={locationRowClickHandler}
              />
            );
          })}
        {hasData === false && (
          <Alert show>
            <Text>{t("inv-dashboard:No records found.")}</Text>
          </Alert>
        )}
        {displayShowMoreButton && (
          <Button variant="transparent" onClick={showMoreClickHandler}>
            <Text underline>{t("inv-dashboard:See More")}</Text>
          </Button>
        )}
        {displayPaginationControls && (
          <PaginationControls
            page={page}
            totalPages={totalPages}
            onPrevClick={handlePrevClick}
            onNextClick={handleNextClick}
          />
        )}
      </FlexColDiv>
    </div>
  );
};
